/**
 * One notice, as the river and the bell draw it. The glyph comes from the
 * event key, the time line is live, and following the row marks it read.
 */

import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { formatCountdown, formatJalaliDate, formatRelativeFa, formatTime } from '@metal/shared'
import { Icon, type IconName } from '@metal/ui'
import { useMarketClock } from '@metal/ui/session'

export type NotificationChannelKey = 'IN_APP' | 'EMAIL' | 'SMS' | 'PUSH'

export interface NotificationItem {
  id: string
  eventKey: string
  facet: string
  /** «معاملات», «مالی» or «سیستم»; the bell prints it under the title. */
  categoryFa?: string | null
  titleFa: string
  bodyFa: string | null
  actionFa: string | null
  route: string | null
  /** Present only on a reminder row. The counter is rendered from it. */
  countdownUntil: string | null
  /** When the notice was created (ISO). The time line is drawn from it, live. */
  at: string
  /** The server's own time line at fetch time, relative for today and a date for older notices. */
  atFa: string
  read: boolean
  channels: NotificationChannelKey[]
}

export interface NotificationFacet {
  key: string
  labelFa: string
  count: number
}

export interface NotificationCategory {
  key: string
  labelFa: string
  count: number
  unread: number
}

export interface NotificationSummaryResponse {
  totalFa: string
  unreadFa: string
  total: number
  unread: number
  facets: NotificationFacet[]
  /** The page's tabs: trade, money and system. */
  categories?: NotificationCategory[]
}

export interface NotificationsResponse {
  items: NotificationItem[]
  page: number
  pageSize?: number
  pageCount: number
  total: number
  asOf: string
  emptyKind?: 'NO_DATA' | 'NO_MATCH'
  messageFa?: string
}

/**
 * The glyph is chosen from the event code, never from the Persian title: the
 * member events are a closed set and each one has its own mark.
 */
export const EVENT_ICON: Record<string, IconName> = {
  'bid.placed': 'arrow-up',
  'bid.changed': 'exchange',
  'bid.betterPriceRequested': 'message',
  'offer.expiringSoon': 'clock',
  'deal.matched': 'check',
  'offer.closedWithoutMatch': 'x',
  'deal.noticeAndCommissionIssued': 'doc',
  'document.decided': 'shield',
  'brand.approved': 'factory',
  'brand.rejected': 'factory',
  'market.dailyDigest': 'chart',
  'claim.stageChanged': 'flag',
  'kyc.decided': 'shield',
  'platform.announcement': 'info',
  'grade.changed': 'medal',
  'grade.abandonment.decided': 'alert',
  'deal.operatorIntervention': 'gavel',
  'claim.outcomeAnnounced': 'flag',
  'bid.notAccepted': 'x',
  'negotiation.message': 'message',
  'desk.reminder': 'clock',
  'commission.paymentDue': 'clock',
  'commission.overdue': 'alert',
  'commission.settled': 'check',
  'wallet.topupCredited': 'wallet',
  'delivery.dueToday': 'truck',
  'kyc.submitted': 'shield',
  'kyc.inReview': 'shield',
  'kyc.activated': 'shield',
  'kyc.periodicDue': 'shield',
  'kyc.appealDecided': 'shield',
  'kyc.documentRequested': 'shield',
  'account.blocked': 'lock',
  'account.reinstated': 'lock',
  'offer.expiringSoonAtRisk': 'clock',
  'offer.expiringSoonNoBid': 'clock',
  'match.remainderOffered': 'exchange',
  'deal.identitiesRevealed': 'users',
  'deal.stageChanged': 'doc',
  'listing.published': 'cathode',
  'listing.reviewDecided': 'cathode',
  'listing.priceChangeDecided': 'cathode',
  'ticket.answered': 'message',
  'claim.deskNote': 'flag',
  'claim.responseDue': 'flag',
  'agency.bound': 'building',
  'agency.released': 'building',
  'deal.buyerPrincipalChanged': 'exchange',
  'deal.counterpartyLate': 'clock',
  'deal.followUpOpened': 'flag',
  'deal.followUpAgainst': 'flag',
  'deal.followUpStep': 'flag',
  'deal.penaltyRaised': 'wallet',
  'deal.restrictionLifted': 'lock',
  'deal.ancillaryRequested': 'shield',
  'deal.ancillaryResult': 'shield',
  'listing.announced': 'cathode',
  'tender.opened': 'lock',
  'tender.decisionDue': 'hourglass',
}

/** The geometry every row shares, whatever element it is drawn as. */
const ROW_STYLE: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '30px minmax(0, 1fr) auto',
  gap: 'var(--s-4)',
  alignItems: 'start',
  inlineSize: '100%',
  paddingBlock: 13,
  paddingInline: 0,
  background: 'none',
  border: 0,
  borderBlockEnd: '1px solid var(--c-border)',
  color: 'inherit',
  font: 'inherit',
  textAlign: 'start',
  textDecoration: 'none',
}

/** The bell's rows: the same columns, closer together. */
const COMPACT_STYLE: CSSProperties = {
  ...ROW_STYLE,
  gap: 'var(--s-3)',
  paddingBlock: 10,
  paddingInline: 'var(--s-3)',
}

export function NotificationRow({
  item,
  onMarkRead,
  compact = false,
  stamp = 'relative',
}: {
  item: NotificationItem
  onMarkRead: () => void
  /** The bell popover: no action line, tighter rows. */
  compact?: boolean
  /**
   * How the time is written. `relative` («۵ دقیقه پیش») where rows are not
   * grouped; under a day heading the heading carries the day, so the row
   * carries its clock time, or its date when the group spans days.
   */
  stamp?: 'relative' | 'time' | 'date'
}) {
  const clock = useMarketClock()
  const remaining = item.countdownUntil ? clock.remaining(item.countdownUntil) : null
  const urgent = remaining !== null && !clock.frozen && remaining <= 3600
  const when = !item.at
    ? item.atFa
    : stamp === 'time'
      ? formatTime(item.at)
      : stamp === 'date'
        ? `${formatJalaliDate(item.at)}، ${formatTime(item.at)}`
        : formatRelativeFa(item.at, clock.now)
  const grouped = stamp !== 'relative'

  const content = (
    <>
      <span
        className="glyph"
        style={{
          inlineSize: 30,
          blockSize: 30,
          display: 'grid',
          placeItems: 'center',
          borderRadius: 'var(--r-sm)',
          background: item.read ? 'var(--p-paper-2)' : 'var(--c-accent-soft)',
          color: item.read ? 'var(--c-fg-muted)' : 'var(--c-fg-accent)',
        }}
      >
        <Icon name={EVENT_ICON[item.eventKey] ?? 'bell'} size="sm" />
      </span>

      <span className="col" style={{ gap: 2, alignItems: 'flex-start', minInlineSize: 0 }}>
        <span className="row" style={{ gap: 7 }}>
          {!item.read && <span className="dot" style={{ background: 'var(--c-accent)' }} />}
          <span className={item.read ? 't-sm' : 't-sm t-sb'}>{item.titleFa}</span>
        </span>
        {compact && item.categoryFa && <span className="t-cap t-muted">{item.categoryFa}</span>}
        {(item.bodyFa || grouped) && (
          <span className="t-cap t-sec">
            {/* Under a day heading the row's own clock leads its line, and a reminder's countdown follows it. */}
            {grouped && <span className="num t-muted">{when}</span>}
            {grouped && remaining !== null && (
              <>
                <span className="t-muted">، </span>
                <span className={`num ${urgent ? 't-sell' : 't-muted'}`}>{`${formatCountdown(remaining)} مانده`}</span>
              </>
            )}
            {grouped && item.bodyFa && <span className="t-muted">، </span>}
            {item.bodyFa}
          </span>
        )}
        {/* A phone has no room for a third column: the verb sits under the text. */}
        {!compact && grouped && item.actionFa && item.route && (
          <span className="btn btn-secondary btn-sm notif-act-under" aria-hidden="true">
            {item.actionFa}
          </span>
        )}
      </span>

      <span style={{ paddingBlockStart: 2, whiteSpace: 'nowrap' }}>
        {remaining !== null && !grouped ? (
          <span className={`t-cap num ${urgent ? 't-sell' : 't-muted'}`}>
            {`${formatCountdown(remaining)} مانده`}
          </span>
        ) : !compact && item.actionFa && item.route ? (
          // The row's own control repeats the verb of its title.
          <span className="btn btn-secondary btn-sm notif-act-end" aria-hidden="true">
            {item.actionFa}
          </span>
        ) : grouped ? null : (
          <span className="t-cap t-muted">{when}</span>
        )}
      </span>
    </>
  )

  // Where it points, it is a link: it opens in a new tab like any other and
  // following it marks it read.
  if (item.route) {
    return (
      <Link to={item.route} onClick={onMarkRead} style={compact ? COMPACT_STYLE : ROW_STYLE}>
        {content}
      </Link>
    )
  }
  // Unread and pointing nowhere: the one thing to do is mark it read.
  if (!item.read) {
    return (
      <button type="button" onClick={onMarkRead} style={{ ...(compact ? COMPACT_STYLE : ROW_STYLE), cursor: 'pointer' }}>
        {content}
      </button>
    )
  }
  return <div style={compact ? COMPACT_STYLE : ROW_STYLE}>{content}</div>
}
